'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { Bell } from 'lucide-react'
import { useUnreadCount } from '@/lib/notifications/use-unread-count'
import { PushPermissionModal, wasPushPromptDismissed } from './push-permission-modal'

interface NotificationBellProps {
  userId: string
}

export function NotificationBell({ userId }: NotificationBellProps) {
  const { count } = useUnreadCount(userId)
  const [prevCount, setPrevCount] = useState(count)
  const [bounce, setBounce] = useState(false)
  const [showPushModal, setShowPushModal] = useState(false)

  // Bounce animation on count change
  useEffect(() => {
    if (count !== prevCount && count > 0) {
      setBounce(true)
      const timer = setTimeout(() => setBounce(false), 300)
      setPrevCount(count)
      return () => clearTimeout(timer)
    }
    setPrevCount(count)
  }, [count, prevCount])

  const badgeLabel = count === 0
    ? ''
    : count > 99
    ? '99+'
    : String(count)

  const handleBellClick = (e: React.MouseEvent) => {
    // If push not dismissed and not already granted, show modal on first click
    if (
      'Notification' in window &&
      Notification.permission === 'default' &&
      !wasPushPromptDismissed()
    ) {
      e.preventDefault()
      setShowPushModal(true)
    }
  }

  return (
    <div className="relative">
      <Link
        href="/notifications"
        onClick={handleBellClick}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-[var(--color-nav-text)]/60 transition-[colors,transform,opacity] duration-100 hover:bg-white/10 hover:text-[var(--color-nav-text)] active:scale-90 active:opacity-70"
        aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
      >
        <Bell className="h-[18px] w-[18px]" />
        {count > 0 && (
          <span
            className={`absolute -right-0.5 -top-0.5 flex h-[14px] min-w-[14px] items-center justify-center rounded-full bg-[#EF4444] px-0.5 text-[8px] font-bold leading-none text-white transition-transform ${
              bounce ? 'scale-125' : 'scale-100'
            }`}
            aria-hidden="true"
          >
            {badgeLabel}
          </span>
        )}
      </Link>

      {/* Live region for screen readers */}
      <div className="sr-only" aria-live="polite" role="status">
        {count > 0 ? `${count} unread notifications` : 'No unread notifications'}
      </div>

      <PushPermissionModal
        isOpen={showPushModal}
        onClose={() => setShowPushModal(false)}
        onEnabled={() => setShowPushModal(false)}
      />
    </div>
  )
}
